import { css } from '@emotion/react';
import { GetServerSideProps, NextPage } from 'next';
import { useRouter } from 'next/router';

import { SponsorCard } from '@/components/expo/SponsorCard';
import { EventsPageSeo } from '@/components/ui/features/events-app/event/EventsPageSeo/EventsPageSeo';
import { EventAppLayout } from '@/components/ui/shared/layouts/EventAppLayout/EventAppLayout';
import { NoTicketCheck } from '@/components/ui/shared/overlays/NoTicketCheck/NoTicketCheck';
import { useAnalytics } from '@/context/Analytics';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useResource, useResources } from '@/lib/datx-jsonapi-react';
import { queryEventSponsorPackagesWithEventSponsors } from '@/queries/event-sponsor-package';
import { queryThinEvent } from '@/queries/thin-events';
import { fetchEventConfiguration } from '@/queries/upgrade/eventConfiguration';
import { EventSponsor } from '@/resources/EventSponsor';
import { ChakraTheme } from '@/styles/themes/default';
import { generateStore } from '@/utils/generateStore';
import { Hydrate, IHydrateProps } from '@/utils/swr/Hydrate';
import { StringParam, urlQueryStringValidate } from '@/utils/url-query-string';

const list = (theme: ChakraTheme) => css`
  display: grid;
  grid-gap: 32px;
  grid-template-columns: 1fr;

  margin-left: auto;
  margin-right: auto;
  margin-top: 24px;

  @media (min-width: ${theme.legacy.breakpoints.Tablet.min}px) {
    &[data-size='small'] {
      grid-template-columns: repeat(3, 1fr);
    }
    &[data-size='medium'] {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  @media (min-width: ${theme.legacy.breakpoints.Desktop.min}px) {
    &[data-size='small'] {
      grid-template-columns: repeat(6, 1fr);
    }
    &[data-size='medium'] {
      grid-template-columns: repeat(4, 1fr);
    }
    &[data-size='big'] {
      grid-template-columns: repeat(2, 1fr);
    }
  }
`;

const ExpoPage: NextPage<IHydrateProps> = ({ fallback }) => {
  const router = useRouter();
  const analytics = useAnalytics();

  const { eventId } = useQueryParams<{ eventId: string }>({
    eventId: StringParam,
  });

  const { data: event } = useResource(queryThinEvent(eventId));
  const { data: eventSponsorPackages } = useResources(
    queryEventSponsorPackagesWithEventSponsors({
      filters: { event_id: eventId, helpdesk: 'false' },
      sort: 'position',
    })
  );

  // TODO: should be a link in the SponsorCard component
  const handleSponsorAction = ({ id, enabledFeatures, slug, website }: EventSponsor) => {
    if (enabledFeatures.directLink) {
      analytics.track('external_link_click', { link: website });

      window.open(website, '__blank');
    } else {
      router.push('/events/[eventId]/expo/[sponsor]', `/events/${event.id}/expo/${id}-${slug}`);
    }
  };

  if (!event || !eventSponsorPackages) {
    return null;
  }

  return (
    <Hydrate fallback={fallback}>
      <EventAppLayout>
        <EventsPageSeo page="expo" />
        <NoTicketCheck eventId={eventId}>
          <div
            css={(theme: ChakraTheme) => css`
              padding: 24px 32px 64px;
              overflow: auto;
              height: 100%;
              background: ${theme.legacy.colors.pageBackground};
            `}
          >
            {eventSponsorPackages
              // Remove packages with no sponsor content
              .filter(({ eventSponsors }) => eventSponsors.length)
              .map(({ id, name, type, eventSponsors }) => (
                <div style={{ marginBottom: '64px' }} key={id}>
                  <h2
                    css={css`
                      font-size: 24px;
                      line-height: calc(32 / 24);
                    `}
                  >
                    {name}
                  </h2>
                  <div css={list} data-size={type}>
                    {eventSponsors.map((eventSponsor) => (
                      <SponsorCard
                        key={eventSponsor.id}
                        cardType={type}
                        eventSponsor={eventSponsor}
                        onActionClick={() => handleSponsorAction(eventSponsor)}
                      />
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </NoTicketCheck>
      </EventAppLayout>
    </Hydrate>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });

  const { eventId } = urlQueryStringValidate<{ eventId: string }>(context.query, {
    eventId: StringParam,
  });

  const eventConfiguration = await fetchEventConfiguration(eventId, context.req.headers.cookie);

  if (!eventConfiguration?.expo_page) {
    return {
      notFound: true,
    };
  }

  const eventResponse = await store.fetchResource(queryThinEvent(eventId));
  const event = eventResponse.data;

  await store.fetchResources(
    queryEventSponsorPackagesWithEventSponsors({ filters: { event_id: event.id, helpdesk: 'false' }, sort: 'position' })
  );

  return {
    props: {
      fallback: store.fallback,
    },
  };
};

export default ExpoPage;
